import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin } from 'lucide-react';

function DataInput() {
  const [formData, setFormData] = useState({
    birth_date: '',
    birth_time: '',
    country: 'India',
    state: '',
    city: '',
    district: '',
    full_name: '',
    gender: 'male',
    father_name: '',
    mother_name: '',
    siblings_count: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editsRemaining, setEditsRemaining] = useState(3);
  const [profileExists, setProfileExists] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCoords, setSelectedCoords] = useState(null);
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const navigate = useNavigate();

  // Load states on mount
  useEffect(() => {
    const loadStates = async () => {
      try {
        const res = await axios.get('/api/locations/states');
        setStates(res.data.states || []);
      } catch (e) {
        console.error('Failed to load states:', e);
      }
    };
    loadStates();
  }, []);

  // Load cities when state changes
  useEffect(() => {
    if (!formData.state) { setCities([]); return; }
    const loadCities = async () => {
      try {
        const res = await axios.get(`/api/locations/cities/${encodeURIComponent(formData.state)}`);
        setCities(res.data.cities || []);
      } catch (e) {
        console.error('Failed to load cities:', e);
      }
    };
    loadCities();
  }, [formData.state]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await axios.get('/api/profile');
        if (response.data.exists) {
          const profile = response.data.profile;
          if (profile.birth_place) {
            const parts = profile.birth_place.split(',').map(p => p.trim());
            if (parts.length >= 3) {
              profile.city = parts[0] || '';
              profile.state = parts[1] || '';
              profile.country = parts[2] || 'India';
            }
          }
          setFormData(prev => ({ ...prev, ...profile }));
          setCitySearch(profile.city || '');
          setProfileExists(true);
          setEditsRemaining(3 - (profile.edit_count || 0));
        }
      } catch (err) {
        console.error('Error loading profile:', err);
      }
    };
    loadProfile();
  }, []);

  // Update coords when city changes
  useEffect(() => {
    if (formData.city && cities.length > 0) {
      const match = cities.find(c => c.city === formData.city);
      if (match) setSelectedCoords({ lat: match.lat, lng: match.lng });
    }
  }, [formData.city, cities]);

  const formatDOB = (value) => {
    const digits = value.replace(/\D/g, '');
    let formatted = '';
    if (digits.length > 0) {
      formatted = digits.substring(0, 2);
      if (digits.length >= 3) formatted += '-' + digits.substring(2, 4);
      if (digits.length >= 5) formatted += '-' + digits.substring(4, 8);
    }
    return formatted;
  };

  const handleDOBChange = (e) => {
    setFormData({ ...formData, birth_date: formatDOB(e.target.value) });
  };

  const handleCitySelect = (city) => {
    setFormData(prev => ({ ...prev, city: city.city }));
    setCitySearch(city.city);
    setSelectedCoords({ lat: city.lat, lng: city.lng });
    setShowCityDropdown(false);
  };

  const filteredCities = citySearch
    ? cities.filter(c => c.city.toLowerCase().includes(citySearch.toLowerCase()))
    : cities;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const birth_place = `${formData.city}, ${formData.state}, ${formData.country}`;
      await axios.post('/api/profile', { ...formData, birth_place });
      const problem = localStorage.getItem('selectedProblem') || 'general';
      localStorage.setItem('userProfile', JSON.stringify({ ...formData, birth_place }));
      navigate('/analysis');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '800px' }}>
        <button onClick={() => navigate('/dashboard')} className="btn btn-ghost" style={{ marginBottom: '2rem' }} data-testid="back-button">
          <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} /> Back to Dashboard
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Birth Details</h1>
          <p style={{ color: '#9CA3AF', marginBottom: '2rem' }}>
            Enter your exact birth details for accurate analysis. {profileExists && `(${editsRemaining} edits remaining)`}
          </p>

          {error && <div className="alert alert-warning" style={{ marginBottom: '1.5rem' }}>{error}</div>}

          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1.5rem' }}>
            {/* Birth Date & Time */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Date of Birth * (DD-MM-YYYY)</label>
                <input type="text" className="form-input" value={formData.birth_date} onChange={handleDOBChange}
                  placeholder="DD-MM-YYYY" maxLength={10} required data-testid="birth-date-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Time of Birth *</label>
                <input type="time" className="form-input" value={formData.birth_time}
                  onChange={(e) => setFormData({ ...formData, birth_time: e.target.value })}
                  required data-testid="birth-time-input" />
              </div>
            </div>

            {/* Birth Place - Country/State */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Country *</label>
                <select className="form-input" value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value, state: '', city: '' })}
                  required data-testid="country-select">
                  <option value="India">India</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">State *</label>
                <select className="form-input" value={formData.state}
                  onChange={(e) => { setFormData({ ...formData, state: e.target.value, city: '' }); setCitySearch(''); setSelectedCoords(null); }}
                  required data-testid="state-select">
                  <option value="">Select State</option>
                  {states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* City (searchable) + District */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group" style={{ position: 'relative' }}>
                <label className="form-label">City / Town *</label>
                <input
                  type="text"
                  className="form-input"
                  value={citySearch}
                  onChange={(e) => { setCitySearch(e.target.value); setShowCityDropdown(true); setFormData(prev => ({ ...prev, city: '' })); setSelectedCoords(null); }}
                  onFocus={() => setShowCityDropdown(true)}
                  placeholder={formData.state ? "Type to search city..." : "Select state first"}
                  disabled={!formData.state}
                  required
                  data-testid="city-search-input"
                  autoComplete="off"
                />
                {showCityDropdown && formData.state && filteredCities.length > 0 && (
                  <div data-testid="city-dropdown" style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '220px', overflowY: 'auto',
                    background: '#1E2139', border: '1px solid #374151', borderRadius: '8px', zIndex: 50,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  }}>
                    {filteredCities.map(c => (
                      <div key={c.city} data-testid={`city-option-${c.city.toLowerCase().replace(/\s/g, '-')}`}
                        onClick={() => handleCitySelect(c)}
                        style={{
                          padding: '0.6rem 0.85rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          borderBottom: '1px solid rgba(55,65,81,0.4)',
                          color: '#E5E7EB', fontSize: '0.9rem',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.15)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <span>{c.city}</span>
                        <span style={{ color: '#9CA3AF', fontSize: '0.75rem' }}>{c.lat.toFixed(2)}, {c.lng.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {showCityDropdown && formData.state && citySearch && filteredCities.length === 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#1E2139', border: '1px solid #374151', borderRadius: '8px', padding: '0.75rem', zIndex: 50 }}>
                    <p style={{ color: '#F59E0B', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>"{citySearch}" not in our list. Searching online...</p>
                    <button type="button" onClick={async () => {
                      try {
                        const res = await axios.post('/api/locations/geocode', { city: citySearch, state: formData.state });
                        if (res.data.cities?.length > 0) {
                          const geocoded = res.data.cities.map(c => ({ city: c.city, lat: c.lat, lng: c.lng }));
                          setCities(prev => [...prev, ...geocoded]);
                        }
                      } catch (e) { console.error(e); }
                    }} style={{ background: 'rgba(201,169,97,0.15)', color: '#C9A961', border: '1px solid #C9A961', borderRadius: '6px', padding: '0.4rem 0.8rem', fontSize: '0.8rem', cursor: 'pointer', width: '100%', marginBottom: '0.5rem' }}>
                      Search "{citySearch}" online
                    </button>
                    <p style={{ color: '#6B7280', fontSize: '0.78rem', margin: 0 }}>Or paste a Google Maps link below:</p>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">District</label>
                <input type="text" className="form-input" value={formData.district}
                  onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                  placeholder="Enter district (optional)" data-testid="district-input" />
              </div>
            </div>

            {/* Google Maps fallback */}
            {!selectedCoords && citySearch && filteredCities.length === 0 && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Paste Google Maps link here (e.g., https://maps.google.com/...)"
                  data-testid="google-maps-input"
                  onChange={async (e) => {
                    const url = e.target.value.trim();
                    if (url.length > 20 && url.includes('google')) {
                      try {
                        const res = await axios.post('/api/locations/parse-google-maps', { url });
                        if (res.data.lat && res.data.lng) {
                          setSelectedCoords({ lat: res.data.lat, lng: res.data.lng });
                          setFormData(prev => ({ ...prev, city: citySearch }));
                          setShowCityDropdown(false);
                        }
                      } catch (e) { console.error(e); }
                    }
                  }}
                  style={{ flex: 1, fontSize: '0.85rem' }}
                />
              </div>
            )}

            {/* Coordinates display */}
            {selectedCoords && (
              <div data-testid="coordinates-display" style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem',
                background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '8px',
              }}>
                <MapPin size={18} color="#10B981" />
                <span style={{ color: '#A7F3D0', fontSize: '0.88rem' }}>
                  Coordinates: <strong>{selectedCoords.lat.toFixed(4)}° N, {selectedCoords.lng.toFixed(4)}° E</strong>
                </span>
                <span style={{ color: '#6B7280', fontSize: '0.78rem', marginLeft: 'auto' }}>Used for Kundali calculation</span>
              </div>
            )}

            {/* Full Name */}
            <div className="form-group">
              <label className="form-label">Full Name (as per certificate) *</label>
              <input type="text" className="form-input" placeholder="Your full legal name" value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} required data-testid="full-name-input" />
            </div>

            {/* Father/Mother Name */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Father's Name</label>
                <input type="text" className="form-input" placeholder="Father's full name" value={formData.father_name}
                  onChange={(e) => setFormData({ ...formData, father_name: e.target.value })} data-testid="father-name-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Mother's Name</label>
                <input type="text" className="form-input" placeholder="Mother's full name" value={formData.mother_name}
                  onChange={(e) => setFormData({ ...formData, mother_name: e.target.value })} data-testid="mother-name-input" />
              </div>
            </div>

            {/* Gender & Siblings */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">Gender *</label>
                <select className="form-input" value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })} data-testid="gender-select">
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Number of Siblings</label>
                <input type="number" className="form-input" min="0" max="15" value={formData.siblings_count}
                  onChange={(e) => setFormData({ ...formData, siblings_count: parseInt(e.target.value) || 0 })} data-testid="siblings-input" />
              </div>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading || !formData.city}
              data-testid="save-profile-btn"
              style={{ width: '100%', fontSize: '1.125rem', padding: '1rem', marginTop: '1rem' }}>
              {loading ? 'Saving...' : profileExists ? 'Update Profile' : 'Save & Continue'}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}

export default DataInput;
